import React, { useEffect, useState, useRef, useCallback } from 'react';
import { LoginForm } from './LoginForm';
import Dialog from '../../common/components/Dialog';
import useClickOutside from '../../hooks/useClickOutsideHandler';
import Icon from '../../common/components/Icon';
import { useTranslations } from '../../localization/useTranslations';
import { LoginModalOpenSource } from '../../common/Auth';

const LoginModal = () => {
  const translations = useTranslations();
  const defaultTitle = `${translations.authLabels.login} / ${translations.authLabels.register}`;

  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState({
    modalTitle: defaultTitle,
    articleTitle: '',
    articleImg: '',
    instruction: '',
  });
  // source of modal open (bookmark, follow-author, follow-topic)
  // comes from data-source attribute of #react-login-modal element
  const [dataSource, setDataSource] = useState<LoginModalOpenSource>(undefined);

  // Memoize the getCustomModalMessage function using useCallback
  const getCustomModalMessage = useCallback(
    (dataSource: LoginModalOpenSource) => {
      switch (dataSource) {
        case 'bookmark':
          return translations.loginModal?.bookmarkMessage || 'Login to bookmark this article.';
        case 'follow-author':
          return translations.loginModal?.followAuthorMessage || 'Login to follow this author.';
        case 'follow-topic':
          return translations.loginModal?.followTopicMessage || 'Login to follow this topic.';
        case 'newsletter':
          return translations.loginModal?.newsletterMessage || 'Login to subscribe to our newsletter.';
        default:
          return '';
      }
    },
    [translations.loginModal]
  );

  // open react login modal from wp
  useEffect(() => {
    const element = document.getElementById('react-login-modal');

    const observer = new MutationObserver((mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.attributeName === 'class') {
          const source = element?.getAttribute('data-source') as LoginModalOpenSource;
          setIsOpen(element?.classList.contains('is-open') || false);
          setDataSource(source);
          setModalData((prevState) => ({
            ...prevState,
            instruction: getCustomModalMessage(source) || '',
          }));
        }
      }
    });

    if (element) {
      observer.observe(element, { attributes: true });
    }

    return () => observer.disconnect();
  }, [getCustomModalMessage]);

  useEffect(() => {
    if (!window.loginModal) return;

    // these are not in use yet, its just placeholder for future use
    const { modalTitle, articleTitle, articleImg } = window.loginModal;

    setModalData((prevState) => ({
      ...prevState,
      modalTitle: modalTitle || defaultTitle,
      articleTitle: articleTitle || '',
      articleImg: articleImg || '',
      instruction: getCustomModalMessage(dataSource) || '',
    }));
  }, [dataSource, defaultTitle, getCustomModalMessage]);

  const handleClose = () => {
    setIsOpen(false);

    // Remove pending actions from local storage if the modal was opened by clicking an action that requires login
    localStorage.removeItem('pendingAction');
    localStorage.removeItem('pendingPremiumNewsletterAction');
    localStorage.removeItem('redirectScrollPosition');
  };

  const modalRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalRef, () => {
    handleClose();
  });

  const { modalTitle, articleTitle, articleImg, instruction } = modalData;

  return (
    <Dialog isOpen={isOpen} onClose={handleClose}>
      <div className="modal modal--small login-modal" ref={modalRef}>
        <div className="modal__wrapper">
          <div className="modal__header login-modal__header">
            <h5>{modalTitle}</h5>
            <button className="login-modal__close-button" onClick={handleClose} aria-label="Close modal">
              <Icon name="close" size="small" />
            </button>
          </div>

          <div className="login-modal__content modal__content">
            {articleTitle.length > 0 && (
              <div className="login-modal__post-details">
                <div className="login-modal__image-wrapper">
                  {articleImg.length > 0 && (
                    <img src={articleImg} alt={articleTitle} className="login-modal__post-image" />
                  )}
                </div>

                <p className="body-text">{articleTitle}</p>
              </div>
            )}

            {articleTitle.length > 0 && instruction.length > 0 && <div className="login-modal__section-divider" />}

            {instruction.length > 0 && (
              <div className="login-modal__description basic-16">{modalData.instruction || ''}</div>
            )}

            <LoginForm isFormInModal />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default LoginModal;
